import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';

/**
 * Accessible modal: click-outside or Esc closes; focus moves into the dialog on
 * open and returns to the trigger on close. Pass `actions` to supply your own
 * footer buttons (e.g. a confirm/cancel pair) — the default lone «بستن» is shown
 * only when no `actions` are provided.
 */
export function Modal({
  title,
  onClose,
  children,
  actions,
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    boxRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus(); // return focus to whatever opened the modal
    };
  }, [onClose]);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box" role="dialog" aria-modal="true" tabIndex={-1} ref={boxRef}>
        {title ? <h2 className="card-title">{title}</h2> : null}
        {children}
        <div className="modal-actions">
          {actions ?? (
            <Button variant="secondary" onClick={onClose}>
              بستن
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
